/**
 * Look acceptance: is the fan looking at the poster?
 *
 * Two questions, deliberately separate:
 *
 * 1. Where is the pin's origin on screen? (`pinNdc`) — the old test, and still
 *    the right one for beacon/remote, where the overlay is small.
 * 2. Where does the poster's *area* reach? (`posterHalfNdc`) — the overlay in
 *    activation is `apparent_size_activation` metres on its longest edge, which
 *    at 14 m can subtend 71° against a 65° camera. Testing only the origin
 *    asked the fan to centre something wider than the screen
 *    (docs/reviews/20261021_qa_corpus_analysis.md §3).
 *
 * Pure: no viewer state, no DOM, and no import of `ar_model.js` (that would
 * pull three.js in and make this module untestable in node). The FOV is the
 * same 65°; `ar_model.js` owns the live constant and `ar_hook.js` passes it.
 */

/** Mirrors `AR_FOV_H` in ar_model.js. Pass `fovH` explicitly when it matters. */
export const LOOK_FOV_H = 65 * (Math.PI / 180)

/** Same grace as the origin test in `projectPin`; 1.0 would clip the frame edge. */
export const NDC_MARGIN = 1.05
/** A poster filling the whole view still has a finite NDC half-extent; cap it. */
export const MAX_HALF_NDC = 4

/**
 * Pin origin in normalised device coordinates.
 *
 * Returns null when the pin is behind the camera plane, which is the one case
 * where "spans the view" is meaningless.
 */
export function pinNdc({heading, pitch = 0, w, h, enu, fovH = LOOK_FOV_H}) {
  if (heading == null || !enu) return null
  const yaw = (heading * Math.PI) / 180
  const pitchRad = (pitch * Math.PI) / 180

  const fwdE = Math.sin(yaw) * Math.cos(pitchRad)
  const fwdN = Math.cos(yaw) * Math.cos(pitchRad)
  const fwdU = Math.sin(pitchRad)

  const rightE = Math.cos(yaw)
  const rightN = -Math.sin(yaw)

  // Camera up = right x forward. With right = (cE, cN, 0) and
  // forward = (sE c, sN c, s) this is (rr, ...) as written in projectPin.
  const upE = rightN * fwdU
  const upN = -rightE * fwdU
  const upU = rightE * fwdN - rightN * fwdE

  const forward = enu.east * fwdE + enu.north * fwdN + enu.up * fwdU
  if (!(forward >= 0.2)) return null

  const right = enu.east * rightE + enu.north * rightN
  const camUp = enu.east * upE + enu.north * upN + enu.up * upU

  const aspect = w / Math.max(h, 1)
  const fovV = 2 * Math.atan(Math.tan(fovH / 2) / aspect)

  return {
    ndcX: right / forward / Math.tan(fovH / 2),
    ndcY: -(camUp / forward) / Math.tan(fovV / 2),
    dist: Math.hypot(enu.east, enu.north, enu.up),
  }
}

/**
 * Half-extent of the poster in NDC, per axis.
 *
 * `sizeM` is the **longest edge** in metres (the convention `ar_model.pose`
 * uses for texture and video planes), and `planeAspect` is the texture's
 * width/height. So the short edge is `sizeM * aspect` in portrait and
 * `sizeM / aspect` in landscape.
 */
export function posterHalfNdc({sizeM, planeAspect, dist, w, h, fovH = LOOK_FOV_H}) {
  const size = Number(sizeM) > 0 ? Number(sizeM) : 1.5
  const aspect = Number(planeAspect) > 0 ? Number(planeAspect) : 1
  const d = Math.max(Number(dist) || 0, 0.3)

  const width = aspect >= 1 ? size : size * aspect
  const height = aspect >= 1 ? size / aspect : size

  const canvasAspect = w / Math.max(h, 1)
  const tanH = Math.tan(fovH / 2)
  const tanV = Math.tan((2 * Math.atan(tanH / canvasAspect)) / 2)

  const halfX = Math.tan(Math.atan(width / 2 / d)) / tanH
  const halfY = Math.tan(Math.atan(height / 2 / d)) / tanV

  return {
    halfX: Math.min(halfX, MAX_HALF_NDC),
    halfY: Math.min(halfY, MAX_HALF_NDC),
  }
}

/** Whether the poster's box reaches the visible frame, origin anywhere. */
export function posterSpansView({ndc, halfX, halfY, margin = NDC_MARGIN}) {
  if (!ndc) return false
  const x = Number.isFinite(ndc.ndcX) ? Math.abs(ndc.ndcX) : Infinity
  const y = Number.isFinite(ndc.ndcY) ? Math.abs(ndc.ndcY) : Infinity
  const hx = Number.isFinite(halfX) ? halfX : 0
  const hy = Number.isFinite(halfY) ? halfY : 0
  return x - hx <= margin && y - hy <= margin
}

/** Strict containment: every edge of the poster is inside the frame. */
export const CONTAIN_MARGIN = 1

export function posterContainedInView({ndc, halfX, halfY, margin = CONTAIN_MARGIN}) {
  if (!ndc) return false
  const x = Number.isFinite(ndc.ndcX) ? Math.abs(ndc.ndcX) : Infinity
  const y = Number.isFinite(ndc.ndcY) ? Math.abs(ndc.ndcY) : Infinity
  const hx = Number.isFinite(halfX) ? halfX : 0
  const hy = Number.isFinite(halfY) ? halfY : 0
  return x + hx <= margin && y + hy <= margin
}

/** Poster wider/taller than the frustum even if you centre it. */
export function posterTooBigToContain({halfX, halfY, margin = CONTAIN_MARGIN}) {
  const hx = Number.isFinite(halfX) ? halfX : 0
  const hy = Number.isFinite(halfY) ? halfY : 0
  return hx > margin || hy > margin
}

function originInFrustum(ndc, margin = NDC_MARGIN) {
  if (!ndc) return false
  const x = Number.isFinite(ndc.ndcX) ? Math.abs(ndc.ndcX) : Infinity
  const y = Number.isFinite(ndc.ndcY) ? Math.abs(ndc.ndcY) : Infinity
  return x <= margin && y <= margin
}

/**
 * Hide the seek chevron: poster is framed, or it cannot be framed and the
 * origin is already in view. A sliver or a pin behind the camera keeps it.
 */
export function hideSeekChevron({ndc, halfX, halfY}) {
  if (posterContainedInView({ndc, halfX, halfY})) return true
  if (posterTooBigToContain({halfX, halfY}) && originInFrustum(ndc)) return true
  return false
}
